/**
 * Permission utility functions for role-based and permission-based access control
 */
export { PERMISSIONS } from './permissionConstants';

/**
 * Helper function to ensure we always have an array of permissions
 * @param {any} permissions - Permissions data (could be array, object, null, undefined)
 * @returns {Array} - Safe array of permissions
 */
export const getSafePermissions = (permissions) => {
  if (Array.isArray(permissions)) return permissions;
  if (permissions === null || permissions === undefined) return [];
  if (typeof permissions === 'object') return Object.values(permissions);
  return [];
};

/**
 * Check if user has a specific permission
 * @param {Array} userPermissions - User's permissions array
 * @param {string} requiredPermission - Permission to check
 * @returns {boolean} - True if user has permission
 */
export const hasPermission = (userPermissions, requiredPermission) => {
  const safePermissions = getSafePermissions(userPermissions);
  try {
    return Array.isArray(safePermissions) && safePermissions.includes(requiredPermission);
  } catch {
    return false;
  }
};

/**
 * Check if user has any of the specified permissions
 * @param {Array} userPermissions - User's permissions array
 * @param {Array} requiredPermissions - Permissions to check (any one)
 * @returns {boolean} - True if user has any of the permissions
 */
export const hasAnyPermission = (userPermissions, requiredPermissions) => {
  const safePermissions = getSafePermissions(userPermissions);
  if (!requiredPermissions?.length) return false;
  return requiredPermissions.some((permission) =>
    safePermissions.includes(permission)
  );
};

/**
 * Check if user has all of the specified permissions
 * @param {Array} userPermissions - User's permissions array
 * @param {Array} requiredPermissions - Permissions to check (all required)
 * @returns {boolean} - True if user has all permissions
 */
export const hasAllPermissions = (userPermissions, requiredPermissions) => {
  if (!userPermissions || !Array.isArray(userPermissions) || !requiredPermissions) {
    return false;
  }
  return requiredPermissions.every(permission => userPermissions.includes(permission));
};

export {
  ADMIN_NAV_ITEMS,
  getDefaultAdminPath,
  getRoutePermissions,
  getPermissionsForRoute,
  ROUTE_PERMISSIONS,
  canAccessRoute,
} from './adminNavConfig';

// Estimate request permissions
export const canViewEstimateRequest = (permissions) => {
  return hasPermission(permissions, 'estimate_request_view');
};

export const canAddEstimateRequest = (permissions) => {
  return hasPermission(permissions, 'estimate_request_add');
};

/**
 * Create a permission checker function for a specific permission
 * @param {Array} userPermissions - User's permissions array
 * @returns {Function} - Function that checks if user has a specific permission
 */
export const createPermissionChecker = (userPermissions) => {
  const safePermissions = getSafePermissions(userPermissions);
  return (permission) => {
    try {
      return Array.isArray(safePermissions) && safePermissions.includes(permission);
    } catch (error) {
      return false;
    }
  };
};
